import React, { FC, useEffect, useRef, useState, MouseEvent } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { currencyFormatter } from "utils";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import { Divider, Grid, Link } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import "moment-timezone";
import InvoicePop from "components/portfolio/components/InvoicePop";
import { useRevenueChange } from "../hooks";
import { Skeleton } from "@mui/material";
import ArrowPopper from "components/app/ArrowPopper";
import { KeyboardArrowDown } from "@mui/icons-material";
import { Account } from "types/portfolio";
import StatusChip from "components/app/StatusChip";
import InvoiceTotal from "../components/InvoiceTotal";
import { classHOC } from "components/app/ClassWrapper";

const useStyles = makeStyles(() => ({
  wrapper: {
    display: "flex",
    alignItems: "center",
    height: 46,
  },
  popWrapper: {
    width: 300,
  },
  rightAlign: {
    textAlign: "right",
    fontSize: 11,
    color: "#898989",
  },
  invoiceStyle: {
    fontSize: 14,
    color: "#121212",
    fontWeight: 500,
  },
  seeAll: {
    display: "flex",
    alignItems: "center",
    height: 40,
    justifyContent: "flex-end",
    paddingRight: 16,
  },
  previousTip: {
    paddingLeft: 20,
  },
  mainTitle: {
    fontSize: 13,
    color: "#333333",
    fontWeight: 500,
  },
  reason: {
    fontSize: 12,
    color: "#535759",
    marginTop: 8,
  },
}));

interface InvoiceProps {
  type: "invoiceMrr" | "invoiceArr";
  label: string;
}

const InvoiceDetail: FC<ICellRendererParams & InvoiceProps> = ({
  data: oriData,
  type,
  label,
  value,
  node,
  column,
  api,
}) => {
  if (node.rowPinned) {
    return column.getColId() === "invoiceArrOnPremAndCloudAndOther" ? (
      <></>
    ) : (
      <InvoiceTotal {...{ column, api, data: oriData }} />
    );
  }
  const data: Account = oriData;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const [status, setStatus] = useState(false);
  const { data: invoice } = useRevenueChange(
    anchorEl ? data?.accountKey : ""
    // anchorEl ? "fff3d798-1f1f-4cea-b8f3-3b4f526a3a57" : "" // for test,
  );
  const popRef = useRef<HTMLDivElement>(null);
  const invoiceCellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !invoiceCellRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const openAllInvoice = () => {
    setStatus(true);
  };

  const handleInvoiceClose = () => {
    setStatus(false);
  };

  const hasPeding = data?.pendingCount > 0;
  const isInactive = data?.accountStatus === "Inactive";

  return (
    <>
      <div
        ref={invoiceCellRef}
        className={classes.wrapper}
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
        }}
      >
        <span>{value === "" ? "" : currencyFormatter(value ?? 0)}</span>
        {/* up/down arrow part */}
        {data?.invoiceMrrTrend === "up" ? (
          <ArrowUpward
            fontSize="small"
            htmlColor={hasPeding ? "#FC9D03" : "#00AB50"}
          />
        ) : data?.invoiceMrrTrend === "down" || isInactive ? (
          <ArrowDownward
            fontSize="small"
            htmlColor={hasPeding ? "#FC9D03" : "#D4371C"}
          />
        ) : null}
        <KeyboardArrowDown style={{ fontSize: "1rem" }} />
      </div>
      {anchorEl ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right"
          disablePortal={false}
          style={{ zIndex: status ? 0 : 2000 }}
        >
          <div
            className={classes.popWrapper}
            ref={popRef}
          >
            <Grid
              container={true}
              direction="column"
            >
              {invoice ? (
                <>
                  {invoice?.slice(0, 3).map((item, index) => {
                    const isPending = item.estimated;
                    return (
                      <Grid
                        item={true}
                        container={true}
                        key={index}
                        direction="column"
                      >
                        <Grid
                          item={true}
                          container={true}
                          justifyContent="space-between"
                          style={{
                            filter: isPending ? "opacity(0.5)" : undefined,
                          }}
                        >
                          <span className={classes.mainTitle}>{`${
                            isPending ? "Estimated" : index === 0 ? "Current" : "Previous"
                          } ${label} USD`}</span>
                          <span className={classes.rightAlign}>
                            {moment(item?.changedDate)
                              .tz("America/Los_Angeles")
                              .format("MMM Do, YYYY (PT)")}
                          </span>
                        </Grid>
                        <Grid
                          item={true}
                          container={true}
                          alignItems="center"
                          style={{ marginTop: 4 }}
                        >
                          <span
                            className={classes.invoiceStyle}
                            style={{
                              filter: isPending ? "opacity(0.5)" : undefined,
                            }}
                          >
                            {currencyFormatter(item?.[type] ?? 0)}
                          </span>
                          {isPending ? (
                            <StatusChip
                              type="PENDING"
                              style={{ marginLeft: 4 }}
                            />
                          ) : null}
                        </Grid>
                        <Grid
                          item={true}
                          style={{ marginTop: 8 }}
                        >
                          <span
                            className={classes.reason}
                            style={{
                              filter: isPending ? "opacity(0.5)" : undefined,
                            }}
                          >
                            {item?.reasonForChange}
                          </span>
                        </Grid>
                        <Divider style={{ margin: "12px 0" }} />
                      </Grid>
                    );
                  })}
                  {invoice?.length ? (
                    <div className={classes.seeAll}>
                      <Link onClick={openAllInvoice}>Show Details</Link>
                    </div>
                  ) : null}
                </>
              ) : !invoice && anchorEl ? (
                <>
                  {Array.from({ length: 9 }).map((x, i) => (
                    <Skeleton
                      key={`${x}${i}`}
                      width={280}
                      height={30}
                      variant="rectangular"
                      style={{ margin: "6px 12px" }}
                    />
                  ))}
                </>
              ) : null}
              {invoice?.length === 0 ? (
                <div className={`${classes.seeAll} ${classes.previousTip}`}>
                  There is no previous {label}.
                </div>
              ) : null}
            </Grid>
          </div>
        </ArrowPopper>
      ) : null}
      <InvoicePop
        type={type}
        label={label}
        invoicePopStatus={status}
        invoiceHandleClose={handleInvoiceClose}
        accountKey={data?.accountKey}
      />
    </>
  );
};
export default classHOC(InvoiceDetail);
